import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Button } from '@img/ui';
import { api, type ApiSuccess } from '../../lib/api';

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  isRead: boolean;
  createdAt: string;
}

export function MyNotificationsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['me', 'notifications'],
    queryFn: () => api.get<ApiSuccess<Notification[]>>('/api/me/notifications'),
  });

  const readAllMutation = useMutation({
    mutationFn: () => api.post('/api/me/notifications/read-all'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'notifications'] }),
  });

  const list = data?.data ?? [];
  const unread = list.filter((n) => !n.isRead).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-black text-navy-dark">الإشعارات{unread > 0 ? ` (${unread} جديد)` : ''}</h1>
        {unread > 0 && (
          <Button variant="primary" onClick={() => readAllMutation.mutate()} loading={readAllMutation.isPending}>
            ✓ تعليم الكل كمقروء
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-gray-500">⟳ جاري التحميل…</div>
      ) : list.length === 0 ? (
        <Card>
          <CardBody className="text-center py-16">
            <div className="text-6xl mb-3">🔔</div>
            <p className="text-gray-600">لا توجد إشعارات بعد.</p>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-2">
          {list.map((n) => (
            <Card key={n.id} className={!n.isRead ? '!border-r-4 !border-r-gold' : ''}>
              <CardBody className="flex items-start gap-3">
                <div className="text-2xl shrink-0">🔔</div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-ink">{n.title}</div>
                  {n.body && <div className="text-sm text-gray-600 mt-1">{n.body}</div>}
                  {n.link && (
                    <a href={n.link} className="text-xs text-navy-dark font-bold hover:text-gold mt-2 inline-block">
                      عرض ←
                    </a>
                  )}
                </div>
                <div className="text-xs text-gray-400 shrink-0">{new Date(n.createdAt).toLocaleString('ar-SA')}</div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
